import { CategoryNavigation, NavigationLink } from "@edgetier/client-components";
import VerifyPermission from "components-for/pages/verify-permission";
import { NavigationHierarchy } from "constants/routes/route-navigation-props";
import { ClientContext } from "context-for/client/client-context";
import { FunctionComponent, memo, useContext } from "react";
import { useLocation } from "react-router-dom";
import { NavigationCategories } from "types-for/routes";
import { isCurrentNavigationGroupActive } from "utilities-for/navigation/is-current-navigation-group-active";

import { IProps } from "./navigation-category-menus.types";
import { useFilterNavigationChildren } from "hooks-for/navigation/use-filter-navigation-children";
import { getRouteHandle } from "constants/routes";

/**
 * The navigation category menus.
 * @param props.isExploreTagsEnabled Whether explore tags is enabled.
 * @param props.isAgentReviewEnabled Whether agent review is enabled.
 */
const NavigationCategoryMenus: FunctionComponent<IProps> = () => {
    const { pathname } = useLocation();
    const client = useContext(ClientContext);
    const filterNavigationChildren = useFilterNavigationChildren();
    return (
        <>
            {(Object.keys(NavigationHierarchy) as Array<keyof typeof NavigationHierarchy>)
                .filter((key) => key !== NavigationCategories.AgentReview || client.isAgentReviewEnabled)
                .map((key) => {
                    const navigationItem = NavigationHierarchy[key];
                    if ("children" in navigationItem) {
                        const isGroupActive = isCurrentNavigationGroupActive(key, pathname);
                        return (
                            <VerifyPermission permission={navigationItem.permission} key={key}>
                                <CategoryNavigation icon={navigationItem.icon} label={key} isActive={isGroupActive}>
                                    {navigationItem.children
                                        .filter(({ pageName }) => filterNavigationChildren(pageName, client))
                                        .map((item) => {
                                            // If the item has a pageName, we can get the route handle and pass the operation type. Will default to OR.
                                            const handle = getRouteHandle(item.pageName);
                                            return (
                                                <VerifyPermission
                                                    permission={item.permission}
                                                    operation={handle?.operation}
                                                    key={item.label}
                                                >
                                                    <NavigationLink {...item} />
                                                </VerifyPermission>
                                            );
                                        })}
                                </CategoryNavigation>
                            </VerifyPermission>
                        );
                    }
                    return filterNavigationChildren(navigationItem.pageName, client) ? (
                        <VerifyPermission permission={navigationItem.permission} key={key}>
                            <NavigationLink {...navigationItem} />
                        </VerifyPermission>
                    ) : null;
                })}
        </>
    );
};

export default memo(NavigationCategoryMenus);
